import { noop } from 'lodash';
import * as React from 'react';
import { DEFAULTS_OPTIONS } from '../editorDefaultOptions';
import { PluginPresetEnum } from '../BlogEditor.types';
import { BackgroundColorPlugin } from '../../DolEditor/marks/background-color/BackgroundColorPlugin';
import { DividerElementPlugin } from '../../DolEditor/elements/divider/plugin';
import { pipe } from '@doltech/utils/lib/pipe';
import { patchFixInvalidNode, trimEmptyParagraph } from '../shared-components/utils';
import { UseBlogEditorReadOnlyPluginProps } from '../BlogEditorPlugin.presets';
import { HeadingReadOnlyPlugin } from '../../../slatePlugins/elements/heading/HeadingReadOnlyPlugin';
import { BorderShadingElementReadOnlyPlugin } from '../../DolEditor/elements/border-shading/BorderShadingReadOnlyPlugin';
import { BulletPointReadOnlyPlugin } from '../plugins/BulletPoint/BulletPointReadOnlyPlugin';
import { QuoteReadOnlyPlugin } from '../plugins/Quote/QuoteReadOnlyPlugin';
import { FaqReadOnlyPlugin } from '../plugins/FAQ/FaqReadOnlyPlugin';
import { getAllExerciseReadOnlyPlugins } from '../plugin-presets/comboExerciseReadOnlyPlugins';
import { getAllTextFormatReadOnlyPlugins } from '../plugin-presets/comboTextFormatReadOnlyPlugins';
import { getAllMediaObjectReadOnlyPlugins } from '../plugin-presets/comboMediaObjectReadOnlyPlugins';
import { getAllSpecialObjectReadOnlyPlugins } from '../plugin-presets/comboSpecialObjectReadOnlyPlugins';
import { BlogTableReadOnlyPluginV2 } from '../../DolEditor/elements/table-blog/BlogTableReadOnlyPluginV2';

export const useBlogEditorReadOnlyPlugin = ({
  onBackWhenInComplete = noop,
  presets = [
    PluginPresetEnum.HEADING,
    PluginPresetEnum.TEXT_FORMAT,
    PluginPresetEnum.BLOCK_FORMAT,
    PluginPresetEnum.HTML_OBJECT,
    PluginPresetEnum.MEDIA_OBJECT,
    PluginPresetEnum.DIVIDER,
    PluginPresetEnum.EXERCISE_OBJECT,
    PluginPresetEnum.SPECIAL_OBJECT,
    PluginPresetEnum.CORE_PARAGRAPH_AT_END,
  ],
  extraPlugins = [],
}: UseBlogEditorReadOnlyPluginProps) => {
  const isReadOnly = true;
  const headingEnabled = true;

  const options = React.useMemo(() => {
    return { ...DEFAULTS_OPTIONS, isReadOnly };
  }, [isReadOnly]);

  const plugins = React.useMemo(() => {
    const corePlugins = [];

    let exercisePlugins = [];
    if (presets.includes(PluginPresetEnum.EXERCISE_OBJECT)) {
      exercisePlugins = getAllExerciseReadOnlyPlugins({ isReadOnly, onBackWhenInComplete });
    }

    const headingPlugins = [
      headingEnabled &&
        presets.includes(PluginPresetEnum.HEADING) &&
        HeadingReadOnlyPlugin(options), // h1, h2, h3, h4, h5
    ];

    let textFormatPlugins = [];
    if (presets.includes(PluginPresetEnum.TEXT_FORMAT)) {
      textFormatPlugins = getAllTextFormatReadOnlyPlugins({ options, isReadOnly });
    }

    let mediaObjectPlugins = [];
    if (presets.includes(PluginPresetEnum.MEDIA_OBJECT)) {
      mediaObjectPlugins = getAllMediaObjectReadOnlyPlugins({ isReadOnly });
    }

    let specialObjectPlugins = [];
    if (presets.includes(PluginPresetEnum.SPECIAL_OBJECT)) {
      specialObjectPlugins = getAllSpecialObjectReadOnlyPlugins({ isReadOnly });
    }

    const usedPlugins = [
      ...textFormatPlugins,
      ...headingPlugins,

      // block format (node)
      presets.includes(PluginPresetEnum.BLOCK_FORMAT) &&
        BorderShadingElementReadOnlyPlugin({ isReadOnly }),
      presets.includes(PluginPresetEnum.BLOCK_FORMAT) && BackgroundColorPlugin(), // node level - mostly use for border and shading
      // presets.includes(PluginPresetEnum.BLOCK_FORMAT) && HoverCardElementPlugin({ isReadOnly }), // anchor link which have popup info

      // html object
      presets.includes(PluginPresetEnum.HTML_OBJECT) && BulletPointReadOnlyPlugin(),
      presets.includes(PluginPresetEnum.HTML_OBJECT) &&
        BlogTableReadOnlyPluginV2({ isReadOnly, stickyHeaderOffset: 64 }),
      presets.includes(PluginPresetEnum.HTML_OBJECT) && QuoteReadOnlyPlugin({ isReadOnly }),
      presets.includes(PluginPresetEnum.HTML_OBJECT) && FaqReadOnlyPlugin({ isReadOnly }),

      ...mediaObjectPlugins, // TODO: rehydrate failed

      // divider
      presets.includes(PluginPresetEnum.DIVIDER) && DividerElementPlugin(), // a divider which show "..."

      ...specialObjectPlugins, // TODO: rehydrate failed
      ...exercisePlugins,
      ...extraPlugins,
      ...corePlugins,
    ];

    return usedPlugins.filter(Boolean);
  }, [extraPlugins, headingEnabled, isReadOnly, onBackWhenInComplete, options, presets]);

  const preprocessReadOnlyInitialValues = React.useCallback(({ initialValue }) => {
    return pipe(patchFixInvalidNode, trimEmptyParagraph)(initialValue);
  }, []);

  const preprocessEditorInitialValues = React.useCallback(({ initialValue }) => {
    return patchFixInvalidNode(initialValue);
  }, []);

  return {
    presets,
    plugins,
    preprocessReadOnlyInitialValues,
    preprocessEditorInitialValues,
  };
};
